//
// Sorteo Component - Form handling and Firebase integration
// ================================================================================

import { getApps, initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously } from 'firebase/auth';
import {
    getFirestore,
    collection as firestoreCollection,
    addDoc,
    serverTimestamp,
} from 'firebase/firestore';

const SORTEO_APP_NAME = 'sorteo';
const REQUIRED_FIREBASE_KEYS = [
    'apiKey',
    'authDomain',
    'projectId',
    'storageBucket',
    'messagingSenderId',
    'appId',
];

let firebaseInitPromise = null;

function getSorteoFirebaseSettings() {
    return window.__SORTEO_FIREBASE__ || null;
}

function getFirestoreCollectionName(settings) {
    return settings?.firestoreCollection || settings?.collection || 'participantes';
}

function buildFirebaseAppConfig(settings) {
    const config = {
        apiKey: String(settings?.apiKey || '').trim(),
        authDomain: String(settings?.authDomain || '').trim(),
        projectId: String(settings?.projectId || '').trim(),
        storageBucket: String(settings?.storageBucket || '').trim(),
        messagingSenderId: String(settings?.messagingSenderId || '').trim(),
        appId: String(settings?.appId || '').trim(),
    };

    const missing = REQUIRED_FIREBASE_KEYS.filter((key) => !config[key]);
    if (missing.length) {
        throw new Error(`Firebase config incompleta: faltan ${missing.join(', ')}`);
    }

    return config;
}

function isFirebaseConfigured(settings) {
    if (!settings) return false;

    try {
        buildFirebaseAppConfig(settings);
        return true;
    } catch {
        return false;
    }
}

function getOrCreateSorteoApp(config) {
    const existing = getApps().find((app) => app.name === SORTEO_APP_NAME);
    if (existing) return existing;
    return initializeApp(config, SORTEO_APP_NAME);
}

async function initSorteoFirebase() {
    if (firebaseInitPromise) {
        return firebaseInitPromise;
    }

    firebaseInitPromise = (async () => {
        try {
            const settings = getSorteoFirebaseSettings();

            if (!isFirebaseConfigured(settings)) {
                console.warn(
                    'Sorteo Firebase: falta apiKey, appId u otra opción obligatoria. ' +
                    'Registra una app web en Firebase Console y completa data.json → config.sorteoFirebase.'
                );
                return null;
            }

            const firebaseConfig = buildFirebaseAppConfig(settings);
            const collectionName = getFirestoreCollectionName(settings);
            const app = getOrCreateSorteoApp(firebaseConfig);
            const auth = getAuth(app);

            const result = await signInAnonymously(auth);
            const db = getFirestore(app);

            return {
                db,
                userId: result.user.uid,
                collectionName,
            };
        } catch (error) {
            firebaseInitPromise = null;
            console.error('Error inicializando Firebase para el sorteo:', error);
            return null;
        }
    })();

    return firebaseInitPromise;
}

async function saveParticipacion(db, collectionName, userId, payload) {
    const data = {
        userId,
        nombre: String(payload.name || '').trim(),
        primer_apellido: String(payload.surname || '').trim(),
        segundo_apellido: String(payload.secondSurname || '').trim(),
        email: String(payload.email || '').trim(),
        telefono: String(payload.tlf || '').trim(),
        provincia: String(payload.province || '').trim(),
        createdAt: serverTimestamp(),
    };

    await addDoc(firestoreCollection(db, collectionName), data);
}

function getSorteoErrorMessage(error) {
    const code = error?.code || '';

    if (code === 'permission-denied') {
        return (
            'No se pudo guardar la participación (permisos de Firestore). ' +
            'Despliega firestore.rules en Firebase Console y activa Auth anónimo.'
        );
    }

    if (code === 'auth/operation-not-allowed') {
        return 'El inicio de sesión anónimo no está activado en Firebase Authentication.';
    }

    return 'No se pudo enviar el formulario. Inténtalo de nuevo más tarde.';
}

function initSorteoModal(element, { modalSelector, openSelector }) {
    const modal = element.querySelector(modalSelector);
    const openLink = element.querySelector(openSelector);
    if (!modal || !openLink || modal.dataset.listenerAdded === 'true') return;

    modal.dataset.listenerAdded = 'true';
    const closeElements = modal.querySelectorAll('[data-sorteo-modal-close]');
    const closeButton = modal.querySelector('.sorteo-modal__close');
    let lastFocusedElement = null;

    const openModal = () => {
        lastFocusedElement = document.activeElement;
        const panel = modal.querySelector('.sorteo-modal__panel');
        if (panel) panel.scrollTop = 0;
        modal.classList.add('--is-open');
        modal.setAttribute('aria-hidden', 'false');
        document.body.classList.add('sorteo-modal-open');
        closeButton?.focus();
    };

    const closeModal = () => {
        modal.classList.remove('--is-open');
        modal.setAttribute('aria-hidden', 'true');
        if (!element.querySelector('.sorteo-modal.--is-open')) {
            document.body.classList.remove('sorteo-modal-open');
        }
        if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
            lastFocusedElement.focus();
        }
    };

    openLink.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        openModal();
    });

    closeElements.forEach((closeEl) => {
        closeEl.addEventListener('click', (event) => {
            event.preventDefault();
            closeModal();
        });
    });

    modal.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            closeModal();
        }
    });
}

function initSorteoForm(element, sorteoId) {
    if (element.dataset.sorteoClosed === 'true') return;

    const formId = `sorteo-form-${sorteoId}`;
    const form = element.querySelector(`#${formId}`);
    if (!form || form.dataset.listenerAdded === 'true') return;

    form.dataset.listenerAdded = 'true';
    const submitButton = form.querySelector('button[type="submit"]');
    const defaultSubmitLabel = submitButton?.textContent?.trim() || 'Enviar';

    form.addEventListener('submit', async (event) => {
        event.preventDefault();

        const name = element.querySelector(`#sorteo-name-${sorteoId}`)?.value?.trim();
        const surname = element.querySelector(`#sorteo-surname-${sorteoId}`)?.value?.trim();
        const secondSurname = element.querySelector(`#sorteo-secondSurname-${sorteoId}`)?.value?.trim();
        const email = element.querySelector(`#sorteo-email-${sorteoId}`)?.value?.trim();
        const tlf = element.querySelector(`#sorteo-tlf-${sorteoId}`)?.value?.trim();
        const province = element.querySelector(`#sorteo-province-${sorteoId}`)?.value;

        if (submitButton) {
            submitButton.disabled = true;
            submitButton.textContent = 'Enviando…';
        }

        try {
            const firebase = await initSorteoFirebase();

            if (!firebase) {
                alert(
                    'El sorteo aún no está configurado. ' +
                    'Completa la app web en Firebase Console (apiKey y appId).'
                );
                return;
            }

            await saveParticipacion(firebase.db, firebase.collectionName, firebase.userId, {
                name,
                surname,
                secondSurname,
                email,
                tlf,
                province,
            });

            form.reset();
            alert('Datos guardados exitosamente.');
        } catch (error) {
            console.error('Error al guardar los datos del sorteo:', error);
            alert(getSorteoErrorMessage(error));
        } finally {
            if (submitButton) {
                submitButton.disabled = false;
                submitButton.textContent = defaultSubmitLabel;
            }
        }
    });
}

export function initSorteoComponent() {
    const settings = getSorteoFirebaseSettings();
    if (settings && !isFirebaseConfigured(settings)) {
        console.warn('Sorteo Firebase: configuración incompleta en window.__SORTEO_FIREBASE__', settings);
    }

    const sorteoElements = document.querySelectorAll('.c-sorteo');

    sorteoElements.forEach((element) => {
        const sorteoId = element.id;
        if (!sorteoId) return;

        if (element.dataset.sorteoInitialized === 'true') return;
        element.dataset.sorteoInitialized = 'true';

        // Sorteo cerrado: sin formulario ni modales de bases/privacidad
        if (element.dataset.sorteoClosed === 'true') return;

        initSorteoModal(element, {
            modalSelector: `#sorteo-bases-modal-${sorteoId}`,
            openSelector: `[data-sorteo-bases-open="${sorteoId}"]`,
        });

        initSorteoModal(element, {
            modalSelector: `#sorteo-privacy-modal-${sorteoId}`,
            openSelector: `[data-sorteo-privacy-open="${sorteoId}"]`,
        });

        initSorteoForm(element, sorteoId);
    });
}
